import api from './api';

export interface AdminUserSummary {
  id: string;
  email: string | null;
  displayName: string | null;
  photoUrl: string | null;
  telegramUserId: string | null;
  tonAddress: string | null;
  createdAt: string | null;
  lastSignInAt: string | null;
  lastActivityAt: string | null;
  disabled: boolean;
  invoicesCount: number;
  paidInvoicesCount: number;
  revenueStars: number;
  emailsSent: number;
  lastEmailAt: string | null;
}

export interface AdminActivityRecord {
  id: string;
  userId: string;
  kind: 'api' | 'page' | 'admin' | 'email' | 'telegram' | 'system';
  action: string;
  meta?: Record<string, any>;
  impersonatedBy?: string | null;
  ip?: string | null;
  userAgent?: string | null;
  at: string;
}

export interface AdminEmailRecord {
  id: string;
  userId: string;
  channel?: 'email' | 'telegram';
  to: string;
  subject: string | null;
  message: string;
  template?: string | null;
  sentBy: string;
  sentAt: string;
}

export interface AdminUserDetail {
  user: AdminUserSummary;
  activity: AdminActivityRecord[];
  invoices: Record<string, any>[];
  payments: Record<string, any>[];
  withdraws: Record<string, any>[];
  emails: AdminEmailRecord[];
  mailConfigured: boolean;
  mailProvider?: 'smtp' | 'resend' | null;
}

export interface ImpersonateResponse {
  token: string;
  returnToken: string;
  target: { id: string; email: string | null; displayName: string | null };
}

export const adminApi = {
  // --- Retraits (admin) ---
  getWithdraws: async (status?: string, limit?: number) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (limit) params.append('limit', limit.toString());
    const response = await api.get(`/admin/withdraws?${params.toString()}`);
    return response.data;
  },
  approveWithdraw: async (id: string) => {
    const response = await api.post(`/admin/withdraws/${id}/approve`);
    return response.data;
  },
  rejectWithdraw: async (id: string, reason: string) => {
    const response = await api.post(`/admin/withdraws/${id}/reject`, { reason });
    return response.data;
  },
  markPaid: async (id: string, externalRef?: string) => {
    const response = await api.post(`/admin/withdraws/${id}/mark-paid`, { externalRef });
    return response.data;
  },
  markFailed: async (id: string, error: string) => {
    const response = await api.post(`/admin/withdraws/${id}/mark-failed`, { error });
    return response.data;
  },

  // --- Session admin / utilisateurs ---
  /** 200 si admin, 403 sinon. */
  me: async (): Promise<{ isAdmin: boolean; uid: string; impersonatedBy: string | null }> => {
    const res = await api.get('/admin/me');
    return res.data;
  },
  listUsers: async (limit = 200): Promise<AdminUserSummary[]> => {
    const res = await api.get('/admin/users', { params: { limit } });
    return res.data;
  },
  getUser: async (id: string): Promise<AdminUserDetail> => {
    const res = await api.get(`/admin/users/${id}`);
    return res.data;
  },
  impersonate: async (id: string): Promise<ImpersonateResponse> => {
    const res = await api.post(`/admin/users/${id}/impersonate`);
    return res.data;
  },
  sendEmail: async (
    id: string,
    body: { subject: string; message: string; template?: string },
  ): Promise<{ id: string; to: string; providerId: string | null }> => {
    const res = await api.post(`/admin/users/${id}/email`, body);
    return res.data;
  },
  sendBulkEmail: async (body: {
    userIds: string[];
    subject: string;
    message: string;
    template?: string;
  }): Promise<{ sent: number; failed: { id: string; error: string }[]; recipients: string[] }> => {
    const res = await api.post('/admin/users/bulk-email', body);
    return res.data;
  },
  sendTelegram: async (
    id: string,
    body: { message: string; template?: string },
  ): Promise<{ id: string; to: string; messageId: number | null }> => {
    const res = await api.post(`/admin/users/${id}/telegram`, body);
    return res.data;
  },
};
